/**
 * Settings → Data Sources (§17). Cards for each registered source
 * (`source_registry` rows): **state and freshness only**. Seeded rows are
 * labelled "Demo snapshot" — never "Connected". This is a settings surface, NOT
 * a per-source browser: there is no inbox / Slack / CRM view anywhere.
 *
 * No item counts, anywhere on this page (epic locked decision 5). It used to
 * total them into an "N items indexed" chip, which is the most misleading form
 * of the number: it reads as a completeness guarantee across systems whose
 * items are not even the same kind of thing, and it is stale the moment any
 * sync lands.
 *
 * A connection whose sync has stopped keeping up reads "Sync issue — contact
 * support" instead of a tick — derived from freshness in `utils/sourceStatus`,
 * so this page and the connect dialog cannot disagree about whether a source is
 * healthy.
 *
 * RETIRED — its route is commented out in `App.tsx` and it is no longer in the
 * sidebar. Connection state now renders inside the "Connect to Data Room"
 * dialog on /upload. Kept (and still tested) so the route can be restored;
 * status wording is shared with that dialog via `utils/sourceStatus`.
 */

import { useCallback, useEffect, useRef, useState } from "react";
import { Database, Globe, type LucideIcon } from "lucide-react";

import affinityLogo from "../assets/affinity.png";
import boxLogo from "../assets/box-icon.svg";
import dealcloudLogo from "../assets/dealcloud-mark.svg";
import driveLogo from "../assets/google-drive-icon.svg";
import dropboxLogo from "../assets/dropbox-icon.svg";
import outlookLogo from "../assets/outlook.webp";
import sharepointLogo from "../assets/sharepoint-icon.svg";
import slackLogo from "../assets/slack.webp";
import ErrorDisplay from "../components/ErrorDisplay";
import SourceTick from "../components/SourceTick";
import { dataSourcesService } from "../services/dataSourcesService";
import type { RegisteredSource } from "../types/dataSource";
import {
	STATUS_LABEL,
	describeSource,
	hasData,
	hasSyncIssue,
} from "../utils/sourceStatus";

/**
 * Per-system visual identity. Vendor connectors use their real mark (`logo`);
 * generic systems with no brand fall back to a lucide glyph (`icon`). Either
 * way the artwork sits inside the same neutral 40px tile, so mismatched logo
 * aspect ratios (DealCloud's mark is ~1.6:1) never distort the grid — the
 * image is contained, never stretched.
 */
interface SystemVisual {
	logo?: string;
	icon?: LucideIcon;
}

const SYSTEM_VISUALS: Record<string, SystemVisual> = {
	dealcloud: { logo: dealcloudLogo },
	affinity: { logo: affinityLogo },
	// The sandbox registers email-shaped comms as `email`; it is Outlook in the
	// product and the API already humanises it as such.
	email: { logo: outlookLogo },
	outlook: { logo: outlookLogo },
	slack: { logo: slackLogo },
	box: { logo: boxLogo },
	drive: { logo: driveLogo },
	dropbox: { logo: dropboxLogo },
	sharepoint: { logo: sharepointLogo },
	crm: { icon: Database },
	web: { icon: Globe },
};

const FALLBACK_VISUAL: SystemVisual = { icon: Database };

/** Stable keys for the loading placeholders (never array indices). */
const SKELETON_ROWS = ["s1", "s2", "s3", "s4", "s5", "s6"] as const;

function SourceCard({ source, now }: { source: RegisteredSource; now: number }) {
	const visual =
		SYSTEM_VISUALS[source.source_system.toLowerCase()] ?? FALLBACK_VISUAL;
	const Icon = visual.icon;
	const detail = describeSource(source, now);
	// A stale connection must not keep the tick. The tick is the affirmative
	// signal on this page, and leaving it on while the tooltip quietly says
	// "Sync issue" would put the reassurance in the glance and the problem in
	// the hover — the exact inversion this ticket exists to remove.
	const syncIssue = hasSyncIssue(source, now);
	const showTick = hasData(source.status) && !syncIssue;

	return (
		<div className="flex items-center gap-3.5 rounded-xl border border-gray-200/80 bg-white p-4 shadow-sm">
			<div className="flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-lg bg-gray-50 ring-1 ring-inset ring-gray-200/70">
				{visual.logo ? (
					<img
						src={visual.logo}
						alt=""
						className="max-h-5 max-w-5 object-contain"
						aria-hidden
					/>
				) : Icon ? (
					<Icon
						className="h-5 w-5 text-gray-500"
						strokeWidth={1.75}
						aria-hidden
					/>
				) : null}
			</div>

			<span className="min-w-0 flex-1 truncate text-base font-semibold text-text-strong">
				{source.display_name}
			</span>

			{showTick ? (
				<SourceTick label={source.display_name} detail={detail} />
			) : syncIssue ? (
				<span
					className="flex-shrink-0 text-xs font-medium text-amber-700"
					title={detail}
					aria-label={`${source.display_name}: ${detail}`}
				>
					{STATUS_LABEL.sync_issue}
				</span>
			) : (
				<span className="flex-shrink-0 text-xs font-medium text-text-muted">
					{STATUS_LABEL.disconnected}
				</span>
			)}
		</div>
	);
}

export default function DataSources() {
	const [sources, setSources] = useState<RegisteredSource[]>([]);
	const [loading, setLoading] = useState(true);
	const [error, setError] = useState<Error | undefined>(undefined);
	const [now, setNow] = useState(() => Date.now());
	const isMountedRef = useRef(true);

	useEffect(() => {
		isMountedRef.current = true;
		return () => {
			isMountedRef.current = false;
		};
	}, []);

	const fetchSources = useCallback(async () => {
		try {
			setLoading(true);
			setError(undefined);
			const rows = await dataSourcesService.listSources();
			if (isMountedRef.current) {
				setSources(rows);
				// Pinned at load so every card's relative age is measured from the
				// same instant, rather than drifting between re-renders.
				setNow(Date.now());
			}
		} catch (err) {
			if (isMountedRef.current) {
				setError(err instanceof Error ? err : new Error(String(err)));
			}
		} finally {
			if (isMountedRef.current) setLoading(false);
		}
	}, []);

	useEffect(() => {
		void fetchSources();
	}, [fetchSources]);

	const stalledSources = sources.filter((s) => hasSyncIssue(s, now));
	// A stale source is counted as connected AND as having a sync issue, which
	// looks like double-counting and is deliberate: it IS connected — there is
	// a live credential and a configured source — it just is not syncing. The
	// alternative reads worse. Subtracting it would show "0 sources connected"
	// to a firm that has connected Box, which is both false and unactionable;
	// the honest summary of one dead Box is "1 source connected · 1 source has
	// a sync issue", i.e. the thing you set up is the thing that is broken.
	const activeSources = sources.filter((s) => hasData(s.status));

	return (
		<div className="mx-auto w-full max-w-3xl px-6 py-8">
			<header className="border-b border-gray-100 pb-5">
				<h1 className="text-xl font-semibold text-text-base">Data Sources</h1>
				<p className="mt-1.5 max-w-2xl text-sm leading-relaxed text-text-muted">
					Connected systems Doppler reads at answer time. Source data surfaces
					only inside answers — there is no separate inbox, channel, or record
					browser.
				</p>
				{!loading && !error && sources.length > 0 && (
					<div className="mt-3 flex items-center gap-2 text-xs font-medium text-text-muted">
						<span className="inline-flex items-center gap-1.5 rounded-full bg-gray-100 px-2.5 py-1">
							{activeSources.length}{" "}
							{activeSources.length === 1 ? "source" : "sources"} connected
						</span>
						{stalledSources.length > 0 && (
							<span className="inline-flex items-center gap-1.5 rounded-full bg-amber-50 px-2.5 py-1 text-amber-700">
								{stalledSources.length}{" "}
								{stalledSources.length === 1 ? "source has" : "sources have"} a
								sync issue
							</span>
						)}
					</div>
				)}
			</header>

			<div className="mt-6">
				{error ? (
					<ErrorDisplay error={error} onRetry={fetchSources} />
				) : loading ? (
					<div className="grid grid-cols-1 gap-3 md:grid-cols-2">
						{SKELETON_ROWS.map((key) => (
							<div
								key={key}
								className="flex items-center gap-3.5 rounded-xl border border-gray-200/80 bg-white p-4"
							>
								<div className="h-10 w-10 flex-shrink-0 animate-pulse rounded-lg bg-gray-100" />
								<div className="h-4 w-2/3 animate-pulse rounded bg-gray-100" />
							</div>
						))}
					</div>
				) : sources.length === 0 ? (
					<div className="rounded-xl border border-dashed border-gray-300 bg-gray-50/50 py-12 text-center text-sm text-text-muted">
						No data sources registered yet.
					</div>
				) : (
					<div className="grid grid-cols-1 gap-3 md:grid-cols-2">
						{sources.map((source) => (
							<SourceCard key={source.public_id} source={source} now={now} />
						))}
					</div>
				)}
			</div>
		</div>
	);
}
